import { Item } from "@andrew-wolf/react-photoswipe-gallery";
import Image from "next/image";
import { useState, useRef } from "react";
import { AiFilterItemType } from "./types";

interface IAiFilterImageSelectorItemProps {
  item: AiFilterItemType;
  index: number;
}

export function AiFilterImageSelectorItem({
  item,
  index,
}: IAiFilterImageSelectorItemProps) {
  const [isThumbnailProcessed, setIsThumbnailProcessed] =
    useState<boolean>(false);
  const [thumbnail, setThumbnail] = useState<string>(item.thumbnail);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleVideoMouseEnter = () => {
    videoRef.current?.play();
  };

  const handleVideoMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const handleVideoClick = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Video filter rendering
  if (item.isVideo) {
    return (
      <>
        <div className="relative">
          <div
            className={`absolute inset-0 pointer-events-none select-none z-50 transition-opacity ${isThumbnailProcessed ? "opacity-0" : "opacity-100"}`}
          >
            <Image
              src="/assets/no-picture2.png"
              width={200}
              height={200}
              className="object-contain aspect-video"
              alt="no image"
            />
          </div>
          <div
            className={`skeleton bg-[#dadcde] rounded-none absolute inset-0 pointer-events-none select-none z-10 transition-opacity ${isThumbnailProcessed ? "opacity-0" : "opacity-100"}`}
          />

          <video
            ref={videoRef}
            src={item.url}
            width={item.width}
            height={item.height}
            muted
            loop
            playsInline
            onMouseEnter={handleVideoMouseEnter}
            onMouseLeave={handleVideoMouseLeave}
            onClick={handleVideoClick}
            onLoadedData={() => setIsThumbnailProcessed(true)}
            onError={() => {
              setIsThumbnailProcessed(true);
            }}
            className={`lg:object-cover w-full lg:w-full select-none ${isThumbnailProcessed ? "cursor-pointer" : ""}`}
          />
        </div>

        {isModalOpen && (
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-80"
            onClick={handleCloseModal}
          >
            <div
              className="relative max-w-4xl max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={handleCloseModal}
                className="absolute -top-10 right-0 text-white text-2xl hover:text-gray-300"
              >
                ✕
              </button>
              <video
                src={item.url}
                controls
                autoPlay
                className="max-w-full max-h-[90vh] rounded"
              />
            </div>
          </div>
        )}
      </>
    );
  }

  // Image filter rendering (original behavior)
  return (
    <Item
      original={item.url}
      thumbnail={thumbnail}
      width={item.width}
      height={item.height}
    >
      {({ ref, open }) => (
        <div className="relative">
          <div
            className={`absolute inset-0 pointer-events-none select-none z-50 transition-opacity ${isThumbnailProcessed ? "opacity-0" : "opacity-100"}`}
          >
            <Image
              src="/assets/no-picture2.png"
              width={200}
              height={200}
              className="object-contain aspect-video"
              alt="no image"
            />
          </div>
          <div
            className={`skeleton bg-[#dadcde] rounded-none absolute inset-0 pointer-events-none select-none z-10 transition-opacity ${isThumbnailProcessed ? "opacity-0" : "opacity-100"}`}
          />

          <Image
            src={thumbnail}
            width={item.width}
            height={item.height}
            className={`lg:object-cover w-full lg:w-full select-none ${isThumbnailProcessed ? "cursor-pointer" : ""}`}
            alt={item.name}
            id={`#${item.id}`}
            onLoad={() => setIsThumbnailProcessed(true)}
            onClick={(e) => {
              open(e);
            }}
            onError={() => {
              setIsThumbnailProcessed(true);
              setThumbnail("/assets/no-picture2.png");
            }}
            ref={ref}
            priority={index < 20}
          />
        </div>
      )}
    </Item>
  );
}